import BaseModel from './BaseModel'
import Location from './Location'
import User from './User'
import handleAxiosError from '../utils/handleAxiosError'

export default class UserSolution extends BaseModel {

  static entity = 'user_solutions'
  static primaryKey = ['userID', 'locationID']

  declare userID: number
  declare locationID: number | null
  declare universalInventoryRead: boolean
  declare universalInventoryWrite: boolean
  declare rateManagementRead: boolean
  declare rateManagementWrite: boolean
  declare blackoutsRead: boolean
  declare blackoutsWrite: boolean
  declare reportingRead: boolean
  declare reportingWrite: boolean
  declare rateParityRead: boolean
  declare rateParityWrite: boolean
  declare maxCapacityRead: boolean
  declare maxCapacityWrite: boolean
  declare terminalRead: boolean
  declare terminalWrite: boolean
  declare uriRead: boolean
  declare uriWrite: boolean
  declare location: Location
  declare user: User

  static fields() {

    return {

      // fields
      userID: this.number(null).nullable(), // the API doesn't return this field - we must infer it from context
      locationID: this.number(null).nullable(),
      // locationName: this.string(''), // the API returns this, but we don't need it
      universalInventoryRead: this.boolean(false),
      universalInventoryWrite: this.boolean(false),
      rateManagementRead: this.boolean(false),
      rateManagementWrite: this.boolean(false),
      blackoutsRead: this.boolean(false),
      blackoutsWrite: this.boolean(false),
      reportingRead: this.boolean(false),
      reportingWrite: this.boolean(false),
      rateParityRead: this.boolean(false),
      rateParityWrite: this.boolean(false),
      maxCapacityRead: this.boolean(false),
      maxCapacityWrite: this.boolean(false),
      terminalRead: this.boolean(false),
      terminalWrite: this.boolean(false),
      uriRead: this.boolean(false),
      uriWrite: this.boolean(false),

      // relationships
      location: this.belongsTo(Location, 'locationID'),
      user: this.belongsTo(User, 'userID'),

    }

  }

  /**
   * API methods
   */

  static fetchByUserID(userID: number) {
    return this.api()
      .get('/users/' + userID + '/user_solutions', {
        dataTransformer: ({ data }) => {
          // The API doesn't include userIDs in the response. Add them manually.
          return data.userSolutions.map((userSolution: any) => {
            userSolution.userID = userID
            return userSolution
          })

        },
      })
      .catch(handleAxiosError)
  }

  static updateForUserID(userID: number, payload: any) {
    // the API expects the payload to be wrapped in an array
    return this.api()
      .put(`/users/${userID}/user_solution`, [payload], {
        dataTransformer: ({ data }) => {
          
          return {
            userID: data[0].user_id,
            locationID: data[0].location_id,
            universalInventoryRead: data[0].universal_inventory_read,
            universalInventoryWrite: data[0].universal_inventory_write,
            rateManagementRead: data[0].rate_management_read,
            rateManagementWrite: data[0].rate_management_write,
            blackoutsRead: data[0].blackouts_read,
            blackoutsWrite: data[0].blackouts_write,
            reportingRead: data[0].reporting_read,
            reportingWrite: data[0].reporting_write,
            rateParityRead: data[0].rate_parity_read,
            rateParityWrite: data[0].rate_parity_write,
            maxCapacityRead: data[0].max_capacity_read,
            maxCapacityWrite: data[0].max_capacity_write,
            uriRead: data[0].uri_read,
            uriWrite: data[0].uri_write,
            terminalRead: data[0].terminal_read,
            terminalWrite: data[0].terminal_write,
          }

        }
      })
      .catch(handleAxiosError)
  }

  static fetchByLocationID(locationID: number) {

    return this.api().get(`/users/user_solutions_by_location/${locationID}`, {
      dataTransformer: ({ data }) => {
        return data.userSolutions
      },
    })

  }

  static updateByLocationID(locationID: number, payload: UpdateUserSolutionByLocationRequest) {

    return this.api().put(`/users/user_solutions_by_location/${locationID}`, payload, {
      dataTransformer: ({ data }) => {
        return data.userSolutions
      },
    })

  }

}

export interface UpdateUserSolutionByLocationRequest {
  userSolutions: UpdateUserSolutionByLocationRequestItem[]
}

export interface UpdateUserSolutionByLocationRequestItem {
  userID: number
  universalInventoryRead: boolean
  universalInventoryWrite: boolean
  rateManagementRead: boolean
  rateManagementWrite: boolean
  blackoutsRead: boolean
  blackoutsWrite: boolean
  reportingRead: boolean
  reportingWrite: boolean
  rateParityRead: boolean
  rateParityWrite: boolean
  maxCapacityRead: boolean
  maxCapacityWrite: boolean
  uriRead?: boolean
  uriWrite?: boolean
  terminalRead?: boolean
  terminalWrite?: boolean
}
